"use client";

import { useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import PeopleCard, { type PersonItem } from "@/components/people/PeopleCard";

// 活動スコア: 実践 × 4 + 発見 × 3 + 反応 × 2 + 連携 × 1
function activityScore(item: PersonItem): number {
  return (item.practiceCount ?? 0) * 4
    + (item.discoveryCount ?? 0) * 3
    + (item.displayScore ?? 0) * 2
    + (item.isClaimed ? 1 : 0);
}

function sortPeople(items: PersonItem[]): PersonItem[] {
  return [...items].sort((a, b) => {
    const scoreDiff = activityScore(b) - activityScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    return (a.name ?? "").localeCompare(b.name ?? "", "ja");
  });
}

export default function PeoplePage() {
  const [people, setPeople] = useState<PersonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`/api/notion-public-users?__bust=${Date.now()}`)
      .then((r) => r.json())
      .then((data) => {
        setPeople(sortPeople(data.items ?? []));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>カタルタ公認ユーザー</h1>
      <p style={{ fontSize: 13, color: "#888", marginBottom: 6 }}>実践・紹介しているみなさん</p>
      <p style={{ fontSize: 12, color: "#bbb", marginBottom: 24 }}>
        カタルタについては{" "}
        <a
          href="https://kataruta.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#bbb", textDecoration: "underline" }}
        >
          公式サイト
        </a>{" "}
        へ
      </p>

      {loading && (
        <p style={{ color: "#aaa", fontSize: 14 }}>読み込み中...</p>
      )}

      {error && (
        <p style={{ color: "#c44", fontSize: 14 }}>読み込みに失敗しました。しばらく待ってから再試行してください。</p>
      )}

      {!loading && !error && people.length === 0 && (
        <p style={{ color: "#aaa", fontSize: 14 }}>まだ登録されている人がいません。</p>
      )}

      <div
        style={{
          display: "grid",
          gap: 16,
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        }}
      >
        {people.map((person) => (
          <PeopleCard key={person.id} item={person} />
        ))}
      </div>

      {/* Participation prompt */}
      <div style={{ marginTop: 40, padding: "20px 24px", background: "#f9f9f9", borderRadius: 10, borderLeft: "3px solid #e0e0e0" }}>
        <p style={{ fontSize: 14, color: "#444", lineHeight: 1.8, margin: 0, marginBottom: 16 }}>
          場を重ねるほど、使い手としての記録が残っていく。<br />
          発見や実践を書き続けることで、あなたのページが育っていきます。
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <a href="/discoveries" style={{ fontSize: 13, color: "#555", border: "1px solid #ccc", borderRadius: 6, padding: "6px 14px", textDecoration: "none", display: "inline-block" }}>
              発見を書き残す
            </a>
            <span style={{ fontSize: 12, color: "#999", marginLeft: 10 }}>
              気づきをメモする
            </span>
          </div>
          <div>
            <a href="/practice/new" style={{ fontSize: 13, color: "#555", border: "1px solid #ccc", borderRadius: 6, padding: "6px 14px", textDecoration: "none", display: "inline-block" }}>
              実践を記録する
            </a>
            <span style={{ fontSize: 12, color: "#999", marginLeft: 10 }}>
              どんな場で使い、何が起きたかを書く
            </span>
          </div>
        </div>
      </div>

      {/* 関心表明フォーム */}
      <InterestForm />
    </div>
  );
}

// -----------------------------------------------
// 関心表明フォーム
// -----------------------------------------------
const LABELS = ["カタリスト", "アンバサダー", "公認ユーザー"] as const;
const LABEL_DESC: Record<string, string> = {
  カタリスト: "継続的に場をつくり続けている方",
  アンバサダー: "カタルタの取り組みを外に発信・紹介している方",
  公認ユーザー: "カタルタで場を持ったことがある方",
};

function InterestForm() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");

  // ログイン済みなら自動入力
  useEffect(() => {
    if (user) {
      setName(user.fullName ?? "");
      setEmail(user.primaryEmailAddress?.emailAddress ?? "");
    }
  }, [user]);

  const toggleLabel = (label: string) =>
    setLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !labels.length) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/interest-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, labels, message: message || null }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div style={{ marginTop: 32, borderTop: "1px solid #eee", paddingTop: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 14, fontWeight: 600, color: "#333", margin: 0, marginBottom: 4 }}>
          カタリスト・アンバサダー・公認ユーザーになりたい方へ
        </p>
        <p style={{ fontSize: 13, color: "#888", margin: 0 }}>
          関心がある方は、こちらから手を挙げてください。運営からご連絡します。
        </p>
      </div>

      {!open && status !== "done" && (
        <button
          onClick={() => setOpen(true)}
          style={{
            fontSize: 13, color: "#555", border: "1px solid #ccc",
            borderRadius: 6, padding: "6px 16px", background: "white",
            cursor: "pointer",
          }}
        >
          ✋ 手を挙げる
        </button>
      )}

      {status === "done" && (
        <p style={{ fontSize: 13, color: "#2a7a4f", background: "#eaf5ee", border: "1px solid #b8dfc8", borderRadius: 6, padding: "8px 14px", display: "inline-block" }}>
          受け取りました。ご連絡します。
        </p>
      )}

      {open && status !== "done" && (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 480, marginTop: 12 }}>
          {/* ラベル選択 */}
          <div>
            <p style={{ fontSize: 12, color: "#888", margin: 0, marginBottom: 8 }}>関心があるラベル（複数選択可）</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {LABELS.map((label) => (
                <label key={label} style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={labels.includes(label)}
                    onChange={() => toggleLabel(label)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 500, color: "#333" }}>{label}</span>
                    <span style={{ color: "#999", marginLeft: 6 }}>{LABEL_DESC[label]}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* 名前 */}
          <div>
            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
              名前
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="山田 太郎"
              style={{ width: "100%", fontSize: 13, border: "1px solid #ddd", borderRadius: 6, padding: "6px 10px", boxSizing: "border-box" }}
            />
          </div>

          {/* メール */}
          <div>
            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
              メールアドレス <span style={{ color: "#c44" }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{ width: "100%", fontSize: 13, border: "1px solid #ddd", borderRadius: 6, padding: "6px 10px", boxSizing: "border-box" }}
            />
          </div>

          {/* 一言 */}
          <div>
            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
              カタルタとの関わりを一言（任意）
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="研修で使っています、など"
              style={{ width: "100%", fontSize: 13, border: "1px solid #ddd", borderRadius: 6, padding: "6px 10px", boxSizing: "border-box", resize: "vertical" }}
            />
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="submit"
              disabled={status === "sending" || !email || !labels.length}
              style={{
                fontSize: 13, color: "white", background: "#444",
                border: "none", borderRadius: 6, padding: "7px 18px",
                cursor: "pointer", opacity: (!email || !labels.length) ? 0.5 : 1,
              }}
            >
              {status === "sending" ? "送信中…" : "送信する"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ fontSize: 12, color: "#aaa", background: "none", border: "none", cursor: "pointer" }}
            >
              キャンセル
            </button>
            {status === "error" && (
              <span style={{ fontSize: 12, color: "#c44" }}>送信に失敗しました。もう一度お試しください。</span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
